<template>
    <v-container fluid v-if="!isLoading" class="flex-column">
        <h1 class="page-title page-title__primary">Параметры адаптации</h1>
        <p>Мы уже выбрали оптимальные параметры адаптации. Вы можете поменять их, при необходимости.</p>

        <v-alert color="success" dark v-if="isFinished">Расчет выполнен успешно</v-alert>
        <v-alert color="red" v-if="hasError">{{taskError}}</v-alert>

        <v-select v-model="optimizeParams.model" :items="models" label="Симулятор" outlined hide-details></v-select>
        <v-radio-group v-model="taskType">
            <div v-for="type in analysisTypes" :key="type.id">
                <v-radio :value="type.code" :label="type.title" :disabled="type.disabled"></v-radio>
                <div class="v-radio__description">{{type.description}}</div>
            </div>
        </v-radio-group>

        <v-expansion-panels>
            <v-expansion-panel :disabled="isFinished">
                <v-expansion-panel-header>Поменять параметры адаптации</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-form>
                        <v-text-field v-model="optimizeParams.covariance" label="Ковариация" type="number" outlined></v-text-field>
                        <v-text-field v-model="optimizeParams.deviation" label="Отклонение" type="number" outlined></v-text-field>
                        <v-text-field v-model="optimizeParams.n_ensemble" label="Количество ансамблей" type="number" outlined></v-text-field>
                    </v-form>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :disabled="isFinished" v-if="needRelativePermCoeffs">
                <v-expansion-panel-header>Поменять стартовые коэффициенты для подбора ОФП</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-form>
                        <v-text-field v-model="optimizeParams.a" label="a" type="number" outlined></v-text-field>
                        <v-text-field v-model="optimizeParams.no" label="no" type="number" outlined><template #label>n<sub>o</sub></template></v-text-field>
                        <v-text-field v-model="optimizeParams.nw" label="nw" type="number" outlined><template #label>n<sub>w</sub></template></v-text-field>
                    </v-form>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :disabled="isFinished">
                <v-expansion-panel-header>Поменять прочие параметры</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-form>
                        <v-switch v-model="flags.mock" label="Тестовый прогон" hide-details></v-switch>
                        <v-switch v-model="flags.includeVr" label="Оптимизировать общий объем пласта" hide-details></v-switch>
                        <v-switch v-model="flags.includeRelativePerm" label="Оптимизировать ОФП" hide-details></v-switch>
                        <v-switch v-model="flags.connectionRelativePerm" label="Оптимизировать ОФП для каждого перетока" hide-details></v-switch>
                        <v-switch v-model="flags.matchConnectionFlow" label="Подгонять все итоговые объемы жидкостей под наблюдаемые значения (а не только нефть)" hide-details></v-switch>
                        <v-switch v-model="flags.logOptimizer" label="Журналировать оптимизатор" hide-details></v-switch>
                        <v-switch v-model="flags.logSimulator" label="Журналировать симулятор" hide-details></v-switch>
                        <v-switch v-model="flags.singleThread" label="Однопоточная оптимизация" hide-details></v-switch>
                    </v-form>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-container>
</template>

<script>
const DEFAULT_RELATIVE_PERMEABILITY = {
    "a": 0.6,
    "no": 2,
    "nw": 2
}

export default {
    data() {
        return {
            isLoading: true,
            taskLoaded: false,
            taskType: 'base',
            optimizeParams: {
                model: 'insim_fpt3d',
                covariance: 0.5,
                deviation: 2.0,
                n_ensemble: 200,
                a: DEFAULT_RELATIVE_PERMEABILITY.a,
                no: DEFAULT_RELATIVE_PERMEABILITY.no,
                nw: DEFAULT_RELATIVE_PERMEABILITY.nw
            },
            flags: {
                mock: false,
                includeVr: true,
                includeRelativePerm: true,
                connectionRelativePerm: false,
                matchConnectionFlow: true,
                logOptimizer: false,
                logSimulator: false,
                singleThread: false
            },
            models: [
                {text: 'INSIM-FPT3D', value: 'insim_fpt3d'},
                {text: 'INSIM-FT', value: 'insim_ft'},
                {text: 'INSIM-BHP', value: 'insim_bhp'},
                {text: 'INSIM', value: 'insim'},
            ]
        }
    },
    created() {
        this.emitInput();
    },
    mounted() {
        if (!this.task) {
            this.loadTask();
        }
        else if (this.task && this.task.problemId !== this.problemId) {
            this.loadTask();
        }
        else {
            this.isLoading = false;
        }

        this.emitInput();
    },
    watch: {
        task: {
            deep: true,
            handler() {
                if (this.task && this.task.params && this.taskLoaded === false) {
                    this.optimizeParams = this.task.params;
                    this.flags = this.task.flags;
                    this.taskLoaded = true;
                }
            }
        },
        flags: {
            deep: true,
            handler() {
                this.emitInput();
            }
        },
        optimizeParams: {
            deep: true,
            handler() {
                this.emitInput();
            }
        },
        taskType() {
            this.emitInput();
        }
    },
    methods: {
        loadTask() {
            this.isLoading = true;
            if (this.taskId) {
                this.$store.dispatch('task/loadItemById', this.taskId);
            }
            else {
                this.$store.dispatch('task/loadItem', this.problemId);
            }
            this.isLoading = false;
        },
        flagsToOptions(flags) {
            let options = 0;
            if (flags.byVolume) {
                options += 1;
            }

            if (flags.byPermeability) {
                options += 2;
            }

            if (flags.everyConnection) {
                options += 4;
            }

            if (flags.byDebit) {
                options += 8;
            }

            return options;
        },
        optionsToFlags(options) {
            let flags = {
                byVolume: false,
                byPermeability: false,
                byDebit: false,
                everyConnection: false,
            }

            if (options >= 8) {
                flags.byDebit = true;
                options -= 8;
            }

            if (options >= 4) {
                flags.everyConnection = true;
                options -= 4;
            }

            if (options >= 2) {
                flags.byPermeability = true;
                options -= 2;
            }

            if (options >= 1) {
                flags.byVolume = true;
            }

            return flags;
        },
        emitInput() {
            let task = Object.assign({}, this.task);
            task.problemId = this.problemId;
            task.type = this.taskType || 'base';
            task.params = Object.assign(task.params || {}, this.optimizeParams);

            task.params.covariance = parseFloat(task.params.covariance);
            task.params.deviation = parseFloat(task.params.deviation);
            task.params.n_ensemble = parseInt(task.params.n_ensemble);

            task.options = this.options;
            task.flags = Object.assign(task.flags || {}, this.flags);

            this.$emit('input', task);
        }
    },
    computed: {
        taskId() {
            return this.$route.params && this.$route.params.itemId
                ? this.$route.params.itemId || null
                : null;
        },
        problemId() {
            return this.$route.params && this.$route.params.id
                ? this.$route.params.id || null
                : null;
        },
        task() {
            return this.$store.state.task.edit || {};
        },
        options() {
            return this.flagsToOptions(this.flags);
        },
        isFinished() {
            return this.task && this.task?.status === 'finished';
        },
        hasError() {
            return this.task && this.task?.status === 'error';
        },
        taskError() {
            return this.task && this.task?.error || 'Неизвестная ошибка';
        },
        needRelativePermCoeffs() {
            //return ['insim_bhp', 'insim_fpt3d'].includes(this.optimizeParams.model);
            return true;
        },
        analysisTypes() {
            return this.$store.state.problem.analysisTypes;
        }
    }
}
</script>

<style scoped>

</style>